import React from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import {
  Modal,
  ModalContent,
  ModalBody,
  ModalFooter,
  Button,
  Spinner,
  Chip,
} from "@heroui/react";
import { useTranslation } from "react-i18next";
import useAxiosPrivate from "@/hooks/useAxiosPrivate";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import SelectComponent from "@/components/common/form/SelectComponent";

import addUserImgModal from "../../../adduser-modal.png";

type FormValues = {
  selectcustomer: {
    value: string;
    label: string;
  };
};

type CustomerGroup = {
  customer_group_id: string;
  customer_group_alias?: string;
  source?: string;
  status?: string;
};

interface AddGroupMemberModalProps {
  isOpen: boolean;
  onClose: () => void;
  customerGroupId: string;
}

export default function AddGroupMemberModal({
  isOpen,
  onClose,
  customerGroupId,
}: Readonly<AddGroupMemberModalProps>) {
  const { t } = useTranslation();
  const axiosPrivate = useAxiosPrivate();
  const queryClient = useQueryClient();
  const [submitting, setSubmitting] = React.useState(false);
  const [selectedCustomerId, setSelectedCustomerId] = React.useState<
    string | null
  >(null);

  const {
    handleSubmit,
    control,
    reset,
    formState: { isValid },
  } = useForm<FormValues>({
    mode: "onChange",
    defaultValues: {
      selectcustomer: { value: "", label: "" },
    },
  });

  const { data: customerGroups = [], isLoading: groupsLoading } = useQuery<
    CustomerGroup[]
  >({
    queryKey: ["CustomerGroups", selectedCustomerId],
    queryFn: async () => {
      const response = await axiosPrivate.get(
        `/customers/settings/${selectedCustomerId}/groups`,
      );
      return response.data;
    },
    enabled: !!selectedCustomerId,
    staleTime: 0,
    gcTime: 0,
  });

  const renderCustomerGroups = () => {
    if (customerGroups.length > 0) {
      return (
        <div className="flex flex-wrap gap-2">
          {customerGroups.map((group) => (
            <Chip
              key={group.customer_group_id}
              size="sm"
              variant="flat"
              color={
                group.customer_group_id === customerGroupId
                  ? "warning"
                  : "default"
              }
            >
              {group.customer_group_alias || group.customer_group_id}
            </Chip>
          ))}
        </div>
      );
    }
    return (
      <div className="text-sm text-default-400 italic">
        {t("customer_no_group")}
      </div>
    );
  };

  const handleClose = () => {
    reset();
    setSelectedCustomerId(null);
    onClose();
  };

  const handleSelectionChange = (selectedOption: any) => {
    setSelectedCustomerId(selectedOption?.value || null);
  };

  const onSubmit: SubmitHandler<FormValues> = async (data) => {
    setSubmitting(true);
    try {
      const customerName =
        data.selectcustomer.label.split("-").map((str) => str.trim())[1] || "";

      const formattedData = {
        group_id: customerGroupId,
        customers: [
          {
            customer_id: data.selectcustomer.value,
            customer_name: customerName,
          },
        ],
        source: "manual",
      };

      await axiosPrivate.post(
        `/customers_group/settings/${customerGroupId}/members`,
        formattedData,
      );

      handleClose();
      queryClient.invalidateQueries({
        queryKey: ["CustomerGroupMembers", customerGroupId],
      });
    } catch (error) {
      console.error("Error adding member to group:", error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      backdrop="opaque"
      placement="top-center"
      size="2xl"
      classNames={{
        backdrop:
          "bg-gradient-to-t from-vistadark/80 to-vistadark/60 backdrop-opacity-20",
      }}
    >
      <ModalContent
        as="form"
        onSubmit={(e) => {
          e.stopPropagation();
          handleSubmit(onSubmit)(e);
        }}
      >
        <ModalBody>
          <div className="pt-5">
            <div className="text-2xl font-bold pt-3 pb-6">
              {t("add_member_to_group")}
            </div>
          </div>

          <div className="space-y-6 min-h-[300px]">
            <SelectComponent
              name="selectcustomer"
              control={control}
              apiUrl="customers"
              endpoint="customers"
              onSelectionChange={handleSelectionChange}
              defaultValue={null}
              customerId={undefined}
              productId={undefined}
              valueConfidence={undefined}
              isModified={false}
            />

            {!selectedCustomerId && (
              <div className="flex justify-center pt-4">
                <img
                  src={addUserImgModal}
                  alt={t("alt_vistaflow_customer_settings")}
                  className="w-48"
                />
              </div>
            )}

            {selectedCustomerId && (
              <div className="pt-2">
                <div className="text-sm text-vistagrey font-semibold mb-2">
                  {t("customer_groups")}
                </div>
                {groupsLoading ? (
                  <Spinner size="sm" color="primary" />
                ) : (
                  renderCustomerGroups()
                )}
              </div>
            )}
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="default" variant="flat" onPress={handleClose}>
            {t("cancel")}
          </Button>
          <Button
            type="submit"
            disabled={!isValid}
            className="bg-vistagreen text-white"
          >
            {submitting ? <Spinner color="default" size="sm" /> : t("add")}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
